import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends HTMLElement {
  #shouldSkipViewportReposition() {
    if (this.closest('.facets--drawer')) return true;

    const isPanelContent =
      this.classList.contains('facets__panel-content') || this.classList.contains('sorting-filter__options');

    return isPanelContent && Boolean(this.closest('.facets--vertical'));
  }

  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    const parentDetails = this.closest('details');
    if (parentDetails instanceof HTMLDetailsElement && !parentDetails.open) {
      this.style.removeProperty('left');
      return;
    }

    if (this.#shouldSkipViewportReposition()) {
      this.style.removeProperty('left');
      this.style.removeProperty('top');
      this.#mutationObserver.takeRecords();
      return;
    }

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    const usesCssTop =
      this.classList.contains('sorting-filter__options') || this.classList.contains('facets__panel-content');

    if (usesCssTop) {
      this.style.removeProperty('top');
    } else {
      this.style.top = OFFSET + 'px';
    }

    this.style.removeProperty('left');

    if (rect.width === 0 && rect.height === 0) {
      this.#mutationObserver.takeRecords();
      return;
    }

    if (rect.right > viewportWidth) {
      const overflowAmount = rect.right - viewportWidth + OFFSET;
      this.style.left = `-${overflowAmount}px`;
    }

    if (rect.left < 0) {
      const overflowAmount = Math.abs(rect.left) + OFFSET;
      this.style.left = `${overflowAmount}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #parentDetails = null;

  #handleDetailsToggle = () => {
    this.#updatePosition();
  };

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    window.addEventListener('resize', this.#resizeListener);
    this.#parentDetails = this.closest('details');
    this.#parentDetails?.addEventListener('toggle', this.#handleDetailsToggle);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.#resizeListener);
    this.#parentDetails?.removeEventListener('toggle', this.#handleDetailsToggle);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
